import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FolderKanban, Loader2, Pencil, Plus, Eye, RefreshCw } from 'lucide-react';
import { typography } from '../../styles/typography';
import { Card, PrimaryButton, SearchBar } from '../ui';
import {
  formatScopeApiError as formatScopeApiErrorImpl,
  interiorScopeApi,
  isScopesStageUnavailable,
} from './interiorScopeApi';
import { buildWizardSteps } from './scopeTemplateEngine';
import TemplateScopeForm, { TemplateScopeNav } from './TemplateScopeForm';

const formatScopeApiError = (err) =>
  typeof formatScopeApiErrorImpl === 'function'
    ? formatScopeApiErrorImpl(err)
    : err?.message || 'Request failed';

const STATUS_STYLES = {
  draft: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  submitted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

const resolveSiteId = (site) =>
  site?.id ??
  site?.site_id ??
  site?.siteId ??
  site?._id ??
  site?.site_code ??
  '';

const resolveSiteName = (site) =>
  site?.name ||
  site?.site_name ||
  site?.siteCode ||
  site?.site_code ||
  'Unnamed Site';

const scopeListLabel = (scope) => {
  const program = scope.program || {};
  return (
    program.project_address ||
    program.client_names ||
    `Scope ${String(scope.scopeId || '').slice(0, 8)}`
  );
};

const ProjectsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSiteId =
    location.state?.siteId ||
    new URLSearchParams(location.search).get('siteId') ||
    '';

  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState(initialSiteId || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [template, setTemplate] = useState(null);
  const [activeStepId, setActiveStepId] = useState('program:A');
  const [isLoading, setIsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [stageUnavailable, setStageUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    interiorScopeApi
      .getLatestTemplate()
      .then((tpl) => {
        if (active) setTemplate(tpl);
      })
      .catch(() => {
        /* list can still work; detail needs template */
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setSitesLoading(true);
    interiorScopeApi
      .getSites()
      .then((list) => {
        if (!active) return;
        const normalized = Array.isArray(list) ? list : list?.sites || [];
        setSites(normalized);
        if (!selectedSiteId && normalized.length) {
          setSelectedSiteId(resolveSiteId(normalized[0]));
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(formatScopeApiError(err));
        setSites([]);
      })
      .finally(() => {
        if (active) setSitesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const loadScopes = useCallback(async () => {
    if (!selectedSiteId) {
      setScopes([]);
      setSelectedId(null);
      setSelectedDetail(null);
      return;
    }
    setIsLoading(true);
    setError('');
    setStageUnavailable(false);
    try {
      const data = await interiorScopeApi.listScopes(selectedSiteId);
      setScopes(data);
      setSelectedId((current) => {
        if (current && data.some((s) => s.scopeId === current)) return current;
        return data[0]?.scopeId || null;
      });
    } catch (err) {
      setError(formatScopeApiError(err));
      setStageUnavailable(isScopesStageUnavailable(err));
      setScopes([]);
      setSelectedId(null);
      setSelectedDetail(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSiteId]);

  useEffect(() => {
    loadScopes();
  }, [loadScopes]);

  useEffect(() => {
    let active = true;
    if (!selectedSiteId || !selectedId) {
      setSelectedDetail(null);
      return undefined;
    }
    setDetailLoading(true);
    interiorScopeApi
      .getScope(selectedSiteId, selectedId)
      .then((scope) => {
        if (!active) return;
        setSelectedDetail(scope);
        setActiveStepId('program:A');
      })
      .catch((err) => {
        if (!active) return;
        setError(formatScopeApiError(err));
        setSelectedDetail(null);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedId, selectedSiteId]);

  const filteredScopes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return scopes;
    return scopes.filter((scope) => {
      const label = scopeListLabel(scope).toLowerCase();
      const client = String(scope.program?.client_names || '').toLowerCase();
      const status = String(scope.status || '').toLowerCase();
      return label.includes(q) || client.includes(q) || status.includes(q);
    });
  }, [scopes, searchTerm]);

  const steps = useMemo(
    () =>
      template && selectedDetail
        ? buildWizardSteps(template, selectedDetail)
        : [],
    [template, selectedDetail]
  );

  const activeStep = useMemo(
    () => steps.find((s) => s.id === activeStepId) || steps[0] || null,
    [steps, activeStepId]
  );

  useEffect(() => {
    if (!steps.length) return;
    if (!steps.some((s) => s.id === activeStepId)) {
      setActiveStepId(steps[0].id);
    }
  }, [steps, activeStepId]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length > 0) {
      setSearchSuggestions(
        scopes
          .map((s) => scopeListLabel(s))
          .filter((name) => name.toLowerCase().includes(value.toLowerCase()))
          .slice(0, 6)
      );
    } else {
      setSearchSuggestions([]);
    }
  };

  const goCreate = () => {
    if (!selectedSiteId) {
      setError('Select a site before creating a scope sheet.');
      return;
    }
    navigate(`/projects/create?siteId=${encodeURIComponent(selectedSiteId)}`, {
      state: { siteId: selectedSiteId },
    });
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,380px)_minmax(240px,280px)_minmax(0,1fr)] gap-6 min-h-[70vh]">
        <Card.Gradient className="p-5 flex flex-col gap-4 min-h-[560px] max-h-[calc(100vh-8rem)]">
          <h3 className={typography.h3} style={{ color: '#000000' }}>
            Scope Sheets
          </h3>

          {stageUnavailable && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 font-['Open_Sans']">
              Scope API returned 403/404 — check CloudFront routes for{' '}
              <code className="text-xs">/sites/*/scopes*</code> and auth/tenant
              JWT. Paste status + body to backend if this persists.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-black mb-2 font-['Open_Sans']">
              Site
            </label>
            <select
              value={selectedSiteId || ''}
              disabled={sitesLoading}
              onChange={(e) => setSelectedSiteId(e.target.value || null)}
              className="w-full h-11 rounded-xl border border-zinc-300 bg-white px-3 text-sm font-['Open_Sans']"
            >
              {sitesLoading && <option value="">Loading sites…</option>}
              {!sitesLoading && sites.length === 0 && (
                <option value="">No sites available</option>
              )}
              {sites.map((site) => {
                const id = resolveSiteId(site);
                return (
                  <option key={id} value={id}>
                    {resolveSiteName(site)}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="flex items-stretch gap-3">
            <div className="flex-1 min-w-0">
              <SearchBar
                placeholder="Search scopes..."
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={(value) => {
                  setSearchTerm(value);
                  setSearchSuggestions([]);
                }}
                suggestions={searchSuggestions}
                onSuggestionClick={(suggestion) => {
                  setSearchTerm(suggestion);
                  setSearchSuggestions([]);
                }}
                className="w-full"
              />
            </div>
            <PrimaryButton
              onClick={goCreate}
              disabled={!selectedSiteId}
              className="!w-auto !min-w-[112px] !h-12 !px-3 !text-sm shrink-0"
            >
              <span className="inline-flex items-center gap-1.5">
                <Plus className="w-4 h-4" aria-hidden />
                New
              </span>
            </PrimaryButton>
          </div>

          <div className="flex-1 rounded-xl border border-white/60 bg-white/50 overflow-hidden flex flex-col min-h-0">
            <div className="px-4 py-3 border-b border-zinc-200/80 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-black font-['Open_Sans']">
                Scopes
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={loadScopes}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-300 bg-white text-black hover:bg-gray-50"
                  title="Refresh"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                {selectedDetail && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/projects/${selectedId}/preview?siteId=${encodeURIComponent(selectedSiteId)}`,
                          { state: { siteId: selectedSiteId } }
                        )
                      }
                      className="inline-flex h-8 items-center gap-1 rounded-lg border border-zinc-300 bg-white px-3 text-xs font-medium text-black font-['Open_Sans'] hover:bg-gray-50"
                    >
                      <Eye className="w-3.5 h-3.5" aria-hidden />
                      Preview
                    </button>
                    <PrimaryButton
                      onClick={() =>
                        navigate(
                          `/projects/${selectedId}/edit?siteId=${encodeURIComponent(selectedSiteId)}`,
                          { state: { siteId: selectedSiteId } }
                        )
                      }
                      className="!w-auto !min-w-0 !h-8 !px-3 !text-xs"
                    >
                      <span className="inline-flex items-center gap-1">
                        <Pencil className="w-3.5 h-3.5" aria-hidden />
                        Edit
                      </span>
                    </PrimaryButton>
                  </>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {isLoading ? (
                <div className="h-40 flex items-center justify-center gap-2 text-gray-600">
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
                  <span className="text-sm font-['Open_Sans']">Loading…</span>
                </div>
              ) : error ? (
                <div className="p-4 text-red-600 text-sm font-['Open_Sans']">
                  {error}
                </div>
              ) : filteredScopes.length === 0 ? (
                <div className="h-40 flex flex-col items-center justify-center gap-2 text-gray-600 px-4 text-center">
                  <FolderKanban className="w-8 h-8" aria-hidden />
                  <p className="text-sm font-['Open_Sans']">No scope sheets yet</p>
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {filteredScopes.map((scope) => {
                    const isSelected = scope.scopeId === selectedId;
                    return (
                      <li key={scope.scopeId}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(scope.scopeId)}
                          className={`w-full text-left rounded-xl px-4 py-3 transition-colors border ${
                            isSelected
                              ? 'bg-white border-[#6BABA0] shadow-sm'
                              : 'bg-white/40 border-transparent hover:bg-white/70 hover:border-zinc-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-black font-['Open_Sans'] truncate">
                                {scopeListLabel(scope)}
                              </p>
                              <p className="text-sm text-gray-600 font-['Open_Sans'] truncate mt-0.5">
                                {scope.program?.client_names || 'No client'}
                              </p>
                            </div>
                            <span
                              className={`shrink-0 text-xs font-medium font-['Open_Sans'] px-2 py-0.5 rounded-full border ${
                                STATUS_STYLES[scope.status] || STATUS_STYLES.draft
                              }`}
                            >
                              {scope.status || 'draft'}
                            </span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </Card.Gradient>

        <Card.Gradient className="p-4 flex flex-col gap-3 min-h-[560px] max-h-[calc(100vh-8rem)]">
          <h4 className={typography.h4} style={{ color: '#000000' }}>
            Sections
          </h4>
          {selectedDetail && template ? (
            <TemplateScopeNav
              steps={steps}
              activeStepId={activeStep?.id}
              onSelect={setActiveStepId}
              className="flex-1 min-h-0"
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-center px-3">
              <p className="text-sm text-gray-600 font-['Open_Sans']">
                Select a scope sheet to view sections
              </p>
            </div>
          )}
        </Card.Gradient>

        <Card.Gradient className="p-6 flex flex-col min-h-[560px] max-h-[calc(100vh-8rem)] overflow-hidden">
          {detailLoading ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-gray-600">
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
              <span className="text-sm font-['Open_Sans']">Loading scope…</span>
            </div>
          ) : !selectedDetail ? (
            <div className="flex-1 rounded-xl border border-dashed border-zinc-300 bg-white/40 flex flex-col items-center justify-center gap-3 text-gray-600 px-6 text-center">
              <FolderKanban className="w-10 h-10" aria-hidden />
              <p className="text-sm font-['Open_Sans']">
                Select a site and scope sheet to review answers
              </p>
            </div>
          ) : !template || !activeStep ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-600 font-['Open_Sans']">
              Loading template…
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-1">
              <TemplateScopeForm
                key={`${selectedDetail.scopeId}-${activeStep.id}`}
                template={template}
                step={activeStep}
                scopeDoc={selectedDetail}
                readOnly
              />
            </div>
          )}
        </Card.Gradient>
      </div>
    </div>
  );
};

export default ProjectsList;
